import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import React, { useEffect, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import './DashboardLayoutDialog.scss'

import { DashboardLayoutOptions, WidgetType } from '../../utils/dashboardLayout'

interface SortableWidgetOrderItemProps {
  id: WidgetType
  label: string
  index: number
  totalItems: number
  onMoveUp: () => void
  onMoveDown: () => void
  moveUpLabel: string
  moveDownLabel: string
}

const SortableWidgetOrderItem: React.FC<SortableWidgetOrderItemProps> = ({
  id,
  label,
  index,
  totalItems,
  onMoveUp,
  onMoveDown,
  moveUpLabel,
  moveDownLabel,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style} className={`dashboard-widget-order__item ${isDragging ? 'is-dragging' : ''}`}>
      <div className="dashboard-widget-order__content">
        <div className="dashboard-widget-order__icon" {...attributes} {...listeners}>
          <i className="bi bi-grip-vertical"></i>
        </div>
        <div className="dashboard-widget-order__label">{label}</div>
        <div className="dashboard-widget-order__actions">
          <Button
            variant="link"
            size="sm"
            className="dashboard-widget-order__btn"
            onClick={(e) => {
              e.stopPropagation()
              onMoveUp()
            }}
            disabled={index === 0}
            title={moveUpLabel}
          >
            <i className="bi bi-chevron-up"></i>
          </Button>
          <Button
            variant="link"
            size="sm"
            className="dashboard-widget-order__btn"
            onClick={(e) => {
              e.stopPropagation()
              onMoveDown()
            }}
            disabled={index === totalItems - 1}
            title={moveDownLabel}
          >
            <i className="bi bi-chevron-down"></i>
          </Button>
        </div>
      </div>
    </div>
  )
}

interface DashboardLayoutDialogProps {
  show: boolean
  onHide: () => void
  onApply: (options: DashboardLayoutOptions) => void
  currentOptions: DashboardLayoutOptions
}

export const DashboardLayoutDialog: React.FC<DashboardLayoutDialogProps> = ({ show, onHide, onApply, currentOptions }) => {
  const { t } = useTranslation()
  const [options, setOptions] = useState<DashboardLayoutOptions>(currentOptions)

  useEffect(() => {
    if (show) {
      setOptions(currentOptions)
    }
  }, [show, currentOptions])

  const handleApply = () => {
    onApply(options)
    onHide()
  }

  const widgetLabels: Record<WidgetType, string> = {
    'consumer-status': t('document_processing'),
    statistics: t('statistics'),
    'saved-views': t('saved_views'),
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = options.widgetOrder.indexOf(active.id as WidgetType)
      const newIndex = options.widgetOrder.indexOf(over.id as WidgetType)

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(options.widgetOrder, oldIndex, newIndex)
        setOptions({ ...options, widgetOrder: newOrder })
      }
    }
  }

  const moveWidget = (index: number, direction: 'up' | 'down') => {
    const newOrder = [...options.widgetOrder]

    if (direction === 'up' && index > 0) {
      ;[newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]]
    } else if (direction === 'down' && index < newOrder.length - 1) {
      ;[newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]]
    }

    setOptions({ ...options, widgetOrder: newOrder })
  }

  return (
    <Modal show={show} onHide={onHide} centered className="dashboard-layout-dialog">
      <Modal.Header closeButton>
        <Modal.Title>{t('organize_dashboard')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold mb-3">{t('widget_order')}</Form.Label>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={options.widgetOrder} strategy={verticalListSortingStrategy}>
                <div className="dashboard-widget-order">
                  {options.widgetOrder.map((widgetType, index) => (
                    <SortableWidgetOrderItem
                      key={widgetType}
                      id={widgetType}
                      label={widgetLabels[widgetType]}
                      index={index}
                      totalItems={options.widgetOrder.length}
                      onMoveUp={() => moveWidget(index, 'up')}
                      onMoveDown={() => moveWidget(index, 'down')}
                      moveUpLabel={t('move_up')}
                      moveDownLabel={t('move_down')}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold mb-3">{t('views_per_row')}</Form.Label>
            <div className="btn-group w-100" role="group">
              <input
                type="radio"
                className="btn-check"
                id="views-1"
                name="viewsPerRow"
                checked={options.viewsPerRow === 1}
                onChange={() => setOptions({ ...options, viewsPerRow: 1 })}
              />
              <label className="btn btn-outline-primary" htmlFor="views-1">
                1
              </label>
              <input
                type="radio"
                className="btn-check"
                id="views-2"
                name="viewsPerRow"
                checked={options.viewsPerRow === 2}
                onChange={() => setOptions({ ...options, viewsPerRow: 2 })}
              />
              <label className="btn btn-outline-primary" htmlFor="views-2">
                2
              </label>
              <input
                type="radio"
                className="btn-check"
                id="views-3"
                name="viewsPerRow"
                checked={options.viewsPerRow === 3}
                onChange={() => setOptions({ ...options, viewsPerRow: 3 })}
              />
              <label className="btn btn-outline-primary" htmlFor="views-3">
                3
              </label>
              <input
                type="radio"
                className="btn-check"
                id="views-4"
                name="viewsPerRow"
                checked={options.viewsPerRow === 4}
                onChange={() => setOptions({ ...options, viewsPerRow: 4 })}
              />
              <label className="btn btn-outline-primary" htmlFor="views-4">
                4
              </label>
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('cancel')}
        </Button>
        <Button variant="primary" onClick={handleApply}>
          {t('apply')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
